/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type MaterialSymbolName } from '@/components/icon/glyphs';

/**
 * The one status vocabulary shared by every feedback primitive — Toast, Inline Alert
 * and Alert Banner (CRCB2B-641 / 644 / 648).
 *
 * WHY THIS FILE EXISTS: the three tickets each named the types differently (toast said
 * success/warning/error/alert/info, alert said neutral/warning/error/success, banner
 * said success/warning/danger/info). Three components hard-coding three vocabularies is
 * the specific failure this module prevents. Figma's own variant axis is `Intent` with
 * values Neutral | Success | Warning | Attention, which is the same four concepts under
 * a third set of names.
 *
 * `alert` is deliberately absent. CRCB2B-641's type list contains an `Alert` bullet, but
 * no Figma treatment defines it and the name collides with Alert the primitive, so it
 * was confirmed a drafting synonym (styleguide-notifications design D1).
 *
 * Name mapping, for anyone reading a ticket or the Figma file alongside the code:
 *
 * | Here      | Figma `Intent` | Colour token (content tier) |
 * | --------- | -------------- | --------------------------- |
 * | `info`    | Neutral        | --foreground (BLACK, not blue) |
 * | `success` | Success        | --success-text #007f00      |
 * | `warning` | Warning        | --warning-text #935900      |
 * | `error`   | Attention      | --error #c21100             |
 */
export type StatusType = 'info' | 'success' | 'warning' | 'error';

export const STATUS_TYPES: readonly StatusType[] = ['info', 'success', 'warning', 'error'];

/**
 * Glyph per status, from the shipped Material Symbols subset.
 *
 * All four are FILLED (the Icon component's default), which is what makes them read as
 * solid badges: a filled `check_circle` is a solid disc with the check knocked out, and
 * filled `warning` is a solid triangle. That matches the Figma treatment exactly.
 *
 * `error` was added to `glyphs.ts` for this change — the subset had `warning` but no
 * distinct error glyph, and the two types must not share one.
 */
export const STATUS_ICON: Record<StatusType, MaterialSymbolName> = {
    info: 'info',
    success: 'check_circle',
    warning: 'warning',
    error: 'error',
};

/**
 * Live-region politeness per status (styleguide-notifications design D3).
 *
 * `role="alert"` is an implicit `aria-live="assertive"` region — it interrupts whatever a
 * screen reader is currently saying. Correct for a payment failure; wrong for "tax
 * calculated at checkout". `role="status"` is polite and waits its turn.
 *
 * This is why the status vocabulary and the announcement behaviour live together: the
 * severity that picks the colour is the same severity that picks the politeness.
 *
 * Caveat: a live region announces on CHANGE. A primitive present in the initial page
 * render announces nothing regardless of role, so static messages need to be reachable
 * through document order and heading structure instead.
 */
export const STATUS_ROLE: Record<StatusType, 'alert' | 'status'> = {
    info: 'status',
    success: 'status',
    warning: 'alert',
    error: 'alert',
};

/**
 * Icon/glyph colour per status, as a Tailwind text utility on the CONTENT tier.
 *
 * The content tier (`--success-text` etc.) is for a coloured glyph or label on a light
 * surface. It is NOT the fill tier (`--success`, `--warning`, `--destructive`), which is
 * for saturated backgrounds with inverse text. The two are different values with
 * different contrast obligations — substituting one for the other is what the
 * design-tokens spec forbids.
 *
 * `info` is `text-foreground` — BLACK. Verified on the Toast and Inline Alert Neutral
 * variants, which bind `--text-color-primary` for the icon. There is no content-tier
 * blue in the token set and the design does not want one here.
 */
export const STATUS_ICON_CLASS: Record<StatusType, string> = {
    info: 'text-foreground',
    success: 'text-success-text',
    warning: 'text-warning-text',
    error: 'text-error',
};
